import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormControlLabel,
    Radio,
    RadioGroup,
    Grid,
    Box,
    Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { fr } from 'date-fns/locale';
import { Auxiliaire } from '../types/auxiliaire';

// UUID generator with fallback for older browsers or HTTP contexts
const generateUUID = (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback UUID v4 generator
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
    });
};

interface ScheduleDialogProps {
    open: boolean;
    onClose: () => void;
    auxiliaires: Auxiliaire[];
    onSave: (schedule: {
        auxiliaire_id: number;
        start_time: string;
        end_time: string;
        start_date: string;
        end_date: string;
        is_recurring: boolean;
        recurrence_group_id?: string;
    }) => void;
    // Optional preselected date (yyyy-MM-dd) to initialize start/end dates when opening
    initialDate?: string;
}

interface Schedule {
    auxiliaire_id: number;
    start_time: string;
    end_time: string;
    start_date: string;
    end_date: string;
    is_recurring: boolean;
    recurrence_group_id?: string;
}

const ScheduleDialog: React.FC<ScheduleDialogProps> = ({
    open,
    onClose,
    auxiliaires,
    onSave,
    initialDate,
}) => {
    const [scheduleType, setScheduleType] = useState<'one-time' | 'recurring'>('one-time');
    const [schedule, setSchedule] = useState<Schedule>({
        auxiliaire_id: auxiliaires.length > 0 ? auxiliaires[0].id : 0,
        start_time: '09:00',
        end_time: '17:00',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        is_recurring: false,
    });

    useEffect(() => {
        if (auxiliaires.length > 0 && !auxiliaires.some(aux => aux.id === schedule.auxiliaire_id)) {
            setSchedule(prev => ({
                ...prev,
                auxiliaire_id: auxiliaires[0].id
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auxiliaires]);

    // When opening with a specific date, initialize start/end dates to that day
    useEffect(() => {
        if (open && initialDate) {
            setSchedule(prev => ({
                ...prev,
                start_date: initialDate,
                end_date: initialDate,
            }));
        }
    }, [open, initialDate]);

    const handleTimeChange = (field: 'start_time' | 'end_time') => (value: unknown) => {
        if (value instanceof Date) {
            const timeString = value.toLocaleTimeString('fr-FR', { hour12: false, hour: '2-digit', minute: '2-digit' });
            setSchedule((prev: Schedule) => ({
                ...prev,
                [field]: timeString
            }));
        }
    };

    const handleDateChange = (field: 'start_date' | 'end_date') => (value: unknown) => {
        if (value instanceof Date) {
            setSchedule((prev: Schedule) => ({
                ...prev,
                [field]: value.toISOString().split('T')[0]
            }));
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setSchedule((prev: Schedule) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : 
                    (name === 'auxiliaire_id') ? parseInt(value) : value,
        }));
    };

    const handleSubmit = () => {
        if (!schedule.auxiliaire_id || !auxiliaires.some(aux => aux.id === schedule.auxiliaire_id)) {
            alert('Please select a valid auxiliaire');
            return;
        }

        const scheduleToSave = {
            ...schedule,
            is_recurring: scheduleType === 'recurring',
            recurrence_group_id: scheduleType === 'recurring' ? generateUUID() : undefined
        };
        onSave(scheduleToSave);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Planifier un auxiliaire</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <RadioGroup
                        row
                        value={scheduleType}
                        onChange={(e) => setScheduleType(e.target.value as 'one-time' | 'recurring')}
                    >
                        <FormControlLabel value="one-time" control={<Radio />} label="Ponctuel" />
                        <FormControlLabel value="recurring" control={<Radio />} label="Récurrent" />
                    </RadioGroup>
                </Box>

                <FormControl fullWidth margin="normal" error={!schedule.auxiliaire_id}>
                    <InputLabel>Auxiliaire</InputLabel>
                    <Select<number>
                        value={schedule.auxiliaire_id}
                        onChange={(e) => handleInputChange(e as React.ChangeEvent<HTMLInputElement>)}
                        label="Auxiliaire"
                        name="auxiliaire_id"
                    >
                        {auxiliaires.map((aux) => (
                            <MenuItem key={aux.id} value={aux.id}>
                                {aux.name} ({aux.category})
                            </MenuItem>
                        ))}
                    </Select>
                    {!schedule.auxiliaire_id && (
                        <Typography variant="caption" color="error">
                            Please select an auxiliaire
                        </Typography>
                    )}
                </FormControl>

                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <DatePicker
                                 label="Date de début"
                                value={new Date(schedule.start_date)}
                                onChange={handleDateChange('start_date')}
                                inputFormat="dd/MM/yyyy"
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth margin="normal" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <DatePicker
                                 label="Date de fin"
                                value={new Date(schedule.end_date)}
                                onChange={handleDateChange('end_date')}
                                inputFormat="dd/MM/yyyy"
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth margin="normal" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TimePicker
                                 label="Heure de début"
                                value={new Date(`2000-01-01T${schedule.start_time}`)}
                                onChange={handleTimeChange('start_time')}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth margin="normal" />
                                )}
                                minTime={new Date(2000, 0, 1, 0, 0)}
                                maxTime={new Date(2000, 0, 1, 23, 59)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TimePicker
                                 label="Heure de fin"
                                value={new Date(`2000-01-01T${schedule.end_time}`)}
                                onChange={handleTimeChange('end_time')}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth margin="normal" />
                                )}
                                minTime={new Date(2000, 0, 1, 0, 0)}
                                maxTime={new Date(2000, 0, 1, 23, 59)}
                            />
                        </Grid>
                    </Grid>
                </LocalizationProvider>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Annuler</Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary"
                    disabled={!schedule.auxiliaire_id || !auxiliaires.some(aux => aux.id === schedule.auxiliaire_id)}
                >
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleDialog; 